import { useMutation, useQueryClient } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import { useOrgId } from 'hooks/index.js';

/**
 * API helper to create or refresh a product template by public URL.
 * If id is provided, the backend refreshes that template; otherwise creates a new one.
 * Returns a normalized body.
 */
export async function upsertProductTemplate({ firebase, orgId, publicUrl, id, catalog, timeoutMs = 60000 }) {
  if (!firebase) throw new Error('Missing firebase');
  if (!orgId) throw new Error('Missing orgId');
  if (!publicUrl) throw new Error('Missing publicUrl');

  const agent = await firebase.getAgent();
  const payload = id ? { id, publicUrl } : { publicUrl };
  const response = await agent
    .post(`/o/${encodeURIComponent(orgId)}/productTemplates?catalog=${catalog}`)
    .send(payload)
    .timeout({ response: timeoutMs, deadline: timeoutMs });

  const body = response?.body || {};
  const {
    id: returnedId,
    productId,
    title,
    thumbnail,
    brand,
    productReference,
    suggestions,
    price,
    publicUrl: returnedPublicUrl,
    productCode,
  } = body;

  return {
    id: returnedId,
    productId,
    title,
    thumbnail,
    brand,
    productReference,
    suggestions,
    price,
    publicUrl: returnedPublicUrl ?? publicUrl,
    productCode,
  };
}

/**
 * Unified hook to create or refresh a product template.
 * - Create: mutate({ publicUrl })
 * - Refresh: mutate({ id, publicUrl })
 * Returns a normalized object with { id, title, thumbnail, brand, productReference, suggestions, price, publicUrl, productCode }.
 */
const useUpsertProductTemplate = (options = {}) => {
  const firebase = useFirebase();
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const { onSuccess, onError, onSettled, catalog = false, timeoutMs = 60000, ...rest } = options || {};

  return useMutation({
    mutationKey: ['upsertProductTemplate', orgId],
    mutationFn: async ({ id, publicUrl }) => {
      return await upsertProductTemplate({ firebase, orgId, publicUrl, id, catalog, timeoutMs });
    },
    onSuccess: (data, variables, context) => {
      // Invalidate product templates lists so UIs stay in sync
      queryClient.invalidateQueries(['productTemplates', orgId]);
      onSuccess?.(data, variables, context);
    },
    onError,
    onSettled,
    ...rest,
  });
};

export default useUpsertProductTemplate;

